import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import './Toast.css';

const Toast = ({ message, type = 'success', duration = 10000, onClose }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    // Start exit animation before actual close
    const exitTimer = setTimeout(() => {
      setIsExiting(true);
    }, duration - 500); // Start exit animation 500ms before close

    // Close the toast
    const closeTimer = setTimeout(() => {
      setIsVisible(false);
      if (onClose) onClose();
    }, duration);

    return () => {
      clearTimeout(exitTimer);
      clearTimeout(closeTimer);
    };
  }, [duration, onClose]);

  if (!isVisible) return null;

  // Parse message for structured content
  const parseMessage = (msg) => {
    if (typeof msg !== 'string') return msg;

    const lines = msg.split('\n').filter(line => line.trim());
    const sections = [];
    let currentSection = { type: 'text', content: [] };

    lines.forEach(line => {
      const trimmed = line.trim();
      
      if (trimmed.startsWith('✅')) {
        sections.push(currentSection);
        currentSection = { type: 'success', content: [trimmed] };
      } else if (trimmed.startsWith('💰')) {
        sections.push(currentSection);
        currentSection = { type: 'amount', content: [trimmed] };
      } else if (trimmed.startsWith('⚠️')) {
        sections.push(currentSection);
        currentSection = { type: 'warning', content: [trimmed] };
      } else if (trimmed.includes('Negative Stock Items:')) {
        sections.push(currentSection);
        currentSection = { type: 'list-header', content: ['Negative Stock Items:'] };
      } else if (trimmed.includes('Inventory Alerts:')) {
        sections.push(currentSection);
        currentSection = { type: 'list-header', content: ['📊 Inventory Alerts:'] };
      } else if (trimmed.startsWith('•')) {
        if (currentSection.type === 'text') {
          currentSection = { type: 'list', content: [trimmed] };
        } else {
          currentSection.content.push(trimmed);
        }
      } else {
        currentSection.content.push(trimmed);
      }
    });
    
    sections.push(currentSection);
    return sections.filter(s => s.content.length > 0);
  };

  const sections = parseMessage(message);

  // Render toast using React Portal to ensure it's at the root level
  const toastContent = (
    <div 
      className={`toast-overlay ${isExiting ? 'toast-exit' : ''}`}
      onClick={(e) => {
        // Close when clicking backdrop
        if (e.target === e.currentTarget) {
          setIsExiting(true);
          setTimeout(() => {
            setIsVisible(false);
            if (onClose) onClose();
          }, 300);
        }
      }}
    >
      <div 
        className={`toast toast-${type} ${isExiting ? 'toast-slide-out' : 'toast-slide-in'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button 
          className="toast-close"
          onClick={() => {
            setIsExiting(true);
            setTimeout(() => {
              setIsVisible(false);
              if (onClose) onClose();
            }, 300);
          }}
          aria-label="Close"
        >
          ✕
        </button>

        {/* Toast content */}
        <div className="toast-content">
          {sections.map((section, idx) => {
            if (section.type === 'success') {
              return (
                <div key={idx} className="toast-section toast-success-section">
                  {section.content.map((line, i) => (
                    <div key={i} className="toast-success-text">{line}</div>
                  ))}
                </div>
              );
            }
            
            if (section.type === 'amount') {
              return (
                <div key={idx} className="toast-section toast-amount-section">
                  {section.content.map((line, i) => (
                    <div key={i} className="toast-amount-text">{line}</div>
                  ))}
                </div>
              );
            }
            
            if (section.type === 'warning') {
              return (
                <div key={idx} className="toast-section toast-warning-section">
                  {section.content.map((line, i) => (
                    <div key={i} className="toast-warning-text">{line}</div>
                  ))}
                </div>
              );
            }
            
            if (section.type === 'list-header') {
              return (
                <div key={idx} className="toast-section toast-list-header">
                  {section.content[0]}
                </div>
              );
            }
            
            if (section.type === 'list') {
              return (
                <div key={idx} className="toast-section toast-list">
                  {section.content.map((line, i) => (
                    <div key={i} className="toast-list-item">{line}</div>
                  ))}
                </div>
              );
            }
            
            return (
              <div key={idx} className="toast-section">
                {section.content.map((line, i) => (
                  <div key={i}>{line}</div>
                ))}
              </div>
            );
          })}
        </div>

        {/* Progress bar */}
        <div className="toast-progress-container">
          <div 
            className="toast-progress-bar"
            style={{ animationDuration: `${duration}ms` }}
          />
        </div>
      </div>
    </div>
  );

  // Use React Portal to render at document.body level
  return ReactDOM.createPortal(toastContent, document.body);
};

export default Toast;
